(function (root) {
  'use strict';
  const MAX_LINES = 50;
  const WATCH_TYPES = new Set(['watch', 'dive watch', 'chronograph']);
  function parseLines(value) {
    if (typeof value !== 'string' || value.length > 1800 || !value) return null;
    const parts = value.split(',');
    if (parts.length > MAX_LINES) return null;
    const seen = new Set();
    const lines = [];
    for (const part of parts) {
      if (!/^[1-9]\d{0,15}:[1-9]\d{0,2}$/.test(part)) return null;
      const [id, quantity] = part.split(':').map(Number);
      if (!Number.isSafeInteger(id) || quantity > 99 || seen.has(id)) return null;
      seen.add(id);
      lines.push({ id, quantity });
    }
    return lines;
  }
  function snapshot(cart, origin, routeRoot) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart');
    const items = cart.items.map(item => ({
      ProductID: String(item.product_id), VariantID: String(item.variant_id),
      ProductName: item.product_title || item.title || '', VariantName: item.variant_title || '',
      ProductType: String(item.product_type || '').trim(),
      IsWatch: WATCH_TYPES.has(String(item.product_type || '').trim().toLowerCase()),
      Quantity: item.quantity, Price: item.final_price / 100,
      LineTotal: item.final_line_price / 100, ImageURL: item.image || '',
      URL: new URL(item.url || routeRoot, origin).href,
      RequiresSpecialHandling: Boolean(item.selling_plan_allocation || item.parent_relationship ||
        (item.item_components && item.item_components.length) || Object.keys(item.properties || {}).length)
    }));
    const serialized = items.map(i => i.VariantID + ':' + i.Quantity).join(',');
    const recoverable = items.length > 0 && !items.some(i => i.RequiresSpecialHandling) && !!parseLines(serialized);
    return {
      SchemaVersion: 2, HasWatch: items.some(i => i.IsWatch && i.Quantity > 0),
      WatchCount: items.reduce((sum, i) => sum + (i.IsWatch ? i.Quantity : 0), 0), Items: items, ItemCount: items.reduce((sum, i) => sum + i.Quantity, 0),
      LineCount: items.length, Currency: cart.currency, CartValue: cart.total_price / 100,
      '$value': cart.total_price / 100, CartEmpty: items.length === 0,
      RecoverySupported: recoverable, RecoveryMode: 'captured_basket',
      RecoveryURL: recoverable ? origin + routeRoot + '?pr_cart_recover=' + encodeURIComponent(serialized) : '',
      CheckoutPermalink: recoverable ? origin + routeRoot + 'cart/' + serialized : ''
    };
  }
  function fingerprint(value) {
    return JSON.stringify([value.Currency, value.CartValue, value.Items.map(i =>
      [i.VariantID, i.Quantity, i.Price, i.LineTotal, i.RequiresSpecialHandling, i.ProductType, i.IsWatch])]);
  }
  function matches(cart, lines) {
    return cart.items.length === lines.length && lines.every(line =>
      cart.items.some(item => Number(item.variant_id) === line.id && item.quantity === line.quantity &&
        !item.selling_plan_allocation && !Object.keys(item.properties || {}).length));
  }
  const core = { parseLines, snapshot, fingerprint, matches };
  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  if (root.__praesidusCartRecovery) return;
  root.__praesidusCartRecovery = true;
  const configElement = document.getElementById('pr-cart-recovery-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const params = new URLSearchParams(location.search);
  const qa = config.qa === true;
  const debug = qa && params.get('pr_cart_debug') === '1';
  const routeRoot = root.Shopify?.routes?.root || '/';
  const originalFetch = root.fetch.bind(root);
  let timer, running = false, dirty = false, latest = null, lastSent = '', captures = 0, events = 0;
  let reason = 'initial', status = 'Starting';
  let panel, output, recoveryLink;
  let testEmail = '', testEnabled = false;
  try { if (qa) testEmail = sessionStorage.getItem('pr_cart_qa_email') || ''; } catch (_) {}
  const labels = config.labels;
  function klaviyoCall(method, ...args) {
    return new Promise((resolve, reject) => {
      const deadline = setTimeout(() => reject(new Error('Klaviyo connection timed out — no confirmed sync')), 12000);
      Promise.resolve().then(() => root.klaviyo[method](...args)).then(value => {
        clearTimeout(deadline); resolve(value);
      }, error => { clearTimeout(deadline); reject(error); });
    });
  }
  function allowed() {
    try { return root.Shopify.customerPrivacy.marketingAllowed() === true; } catch (_) { return false; }
  }
  async function readCart() {
    const response = await originalFetch(routeRoot + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart read failed');
    return response.json();
  }
  function paint() {
    if (!output) return;
    output.textContent = JSON.stringify({ mode: testEnabled ? 'QA — restricted test profile' : 'QA — no Klaviyo events sent', status, source: reason,
      captures, distinctSnapshots: events, marketingAllowed: allowed(), snapshot: latest }, null, 2);
    recoveryLink.hidden = !latest?.RecoveryURL;
    if (latest?.RecoveryURL) {
      const url = new URL(latest.RecoveryURL);
      url.searchParams.set('preview_theme_id', config.themeId);
      url.searchParams.set('pr_cart_debug', '1');
      recoveryLink.href = url.href;
    }
  }
  async function deliver(value) {
    const fp = fingerprint(value);
    if (fp === lastSent) return;
    if (qa && !testEnabled) { lastSent = fp; events++; status = 'Captured locally'; return; }
    if (!allowed()) { status = 'Waiting for marketing permission'; return; }
    if (!root.klaviyo || typeof root.klaviyo.isIdentified !== 'function') return;
    const identified = await klaviyoCall('isIdentified');
    if (!identified || !allowed()) { status = 'Waiting for identified visitor'; return; }
    const current = { ...value, CapturedAt: new Date().toISOString(), SnapshotID: crypto.randomUUID() };
    if (qa && current.RecoveryURL) {
      const recovery = new URL(current.RecoveryURL);
      recovery.searchParams.set('preview_theme_id', config.themeId);
      current.RecoveryURL = recovery.href;
    }
    const prefix = qa ? 'pr_cart_qa_' : 'pr_cart_';
    const properties = {
      [prefix + 'latest']: current,
      [prefix + 'count']: current.ItemCount,
      [prefix + 'has_watch']: current.HasWatch,
      [prefix + 'watch_count']: current.WatchCount,
      [prefix + 'recoverable']: current.RecoverySupported,
      [prefix + 'updated_at']: current.CapturedAt
    };
    if (qa) properties.email = testEmail;
    await klaviyoCall('identify', properties);
    if (dirty || !allowed()) return;
    await klaviyoCall('track', qa ? 'Praesidus Cart QA Updated' : 'Praesidus Cart Updated', current);
    latest = current;
    lastSent = fp;
    events++;
    status = 'Sent';
  }
  async function capture() {
    if (running) { dirty = true; return; }
    running = true;
    dirty = false;
    try {
      const cart = await readCart();
      if (dirty) return;
      latest = snapshot(cart, location.origin, routeRoot);
      captures++;
      await deliver(latest);
    } catch (error) { status = error.message || 'Capture unavailable — shopping unaffected'; }
    finally { running = false; paint(); if (dirty) schedule('cart_changed_during_read'); }
  }
  function schedule(source) {
    reason = source;
    if (running) dirty = true;
    clearTimeout(timer);
    timer = setTimeout(capture, 450);
  }
  async function enableTest(email) {
    if (!qa || !config.testRecipientHash) return;
    const normalized = email.trim().toLowerCase();
    const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized))))
      .map(byte => byte.toString(16).padStart(2, '0')).join('');
    if (digest !== config.testRecipientHash) throw new Error('Use the approved test address.');
    if (!allowed()) throw new Error('Marketing permission is required for this test.');
    if (typeof root.klaviyo?.identify !== 'function') throw new Error('Klaviyo is still loading. Try again.');
    status = 'Connecting approved test profile'; paint();
    await klaviyoCall('identify', { email: normalized });
    testEmail = normalized;
    testEnabled = true;
    lastSent = '';
    try { sessionStorage.setItem('pr_cart_qa_email', normalized); } catch (_) {}
    schedule('test_enabled');
  }
  function isCartWrite(input, options) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      const method = String(options?.method || input?.method || 'GET').toUpperCase();
      return url.origin === location.origin && method === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch (_) { return false; }
  }
  root.fetch = function (input, options) {
    const request = originalFetch(input, options);
    if (isCartWrite(input, options)) request.then(response => {
      if (response.ok) schedule('shopify_cart_write');
    }, () => {});
    return request;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const observed = new WeakMap();
  XMLHttpRequest.prototype.open = function (method, url) {
    observed.set(this, isCartWrite(String(url), { method }));
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (observed.get(this)) this.addEventListener('load', function () {
      if (this.status >= 200 && this.status < 300) schedule('shopify_cart_write');
    }, { once: true });
    return xhrSend.apply(this, arguments);
  };
  ['cart:change', 'variant:add', 'cart:refresh'].forEach(name =>
    document.addEventListener(name, () => schedule(name)));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule('page_visible');
  });
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) lastSent = '';
    schedule('privacy_changed');
  });
  root.addEventListener('klaviyoForms', event => {
    if (event.detail?.type === 'submit') setTimeout(() => schedule('visitor_identified'), 1500);
  });
  root.addEventListener('pageshow', () => schedule('page_visible'));
  if (debug) {
    panel = document.createElement('details');
    panel.className = 'pr-cart-debug';
    panel.open = true;
    const title = document.createElement('summary');
    title.textContent = 'Cart capture QA';
    output = document.createElement('pre');
    recoveryLink = document.createElement('a');
    recoveryLink.textContent = 'Test recovery link';
    recoveryLink.hidden = true;
    panel.append(title, recoveryLink, output);
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.setAttribute('aria-label', 'Approved QA email');
    emailInput.placeholder = 'Approved QA email';
    const enable = document.createElement('button');
    enable.type = 'button';
    enable.textContent = 'Enable restricted Klaviyo test';
    enable.addEventListener('click', async () => {
      try { await enableTest(emailInput.value); } catch (error) { status = error.message; paint(); }
    });
    const start = document.createElement('button');
    start.type = 'button';
    start.textContent = 'Start QA email sequence';
    start.addEventListener('click', async () => {
      if (!testEnabled || !allowed() || !latest?.RecoverySupported || running) {
        status = 'Enable the test and wait for a recoverable basket to sync first.'; paint(); return;
      }
      start.disabled = true;
      try {
        await root.klaviyo.track('Praesidus Cart QA Started', {
          TriggerSnapshotID: latest.SnapshotID, TriggerItemCount: latest.ItemCount,
          TriggerItems: latest.Items, RunID: crypto.randomUUID()
        });
        status = 'QA sequence requested';
      } catch (_) { status = 'QA start failed'; }
      finally { start.disabled = false; paint(); }
    });
    const stop = document.createElement('button');
    stop.type = 'button';
    stop.textContent = 'Stop restricted test';
    stop.addEventListener('click', () => {
      testEnabled = false; testEmail = '';
      try { sessionStorage.removeItem('pr_cart_qa_email'); } catch (_) {}
      status = 'Restricted test stopped'; paint();
    });
    panel.append(emailInput, enable, start, stop);
    document.body.append(panel);
  }
  function showRecovery() {
    if (!params.has('pr_cart_recover')) return;
    const lines = parseLines(params.get('pr_cart_recover'));
    const dialog = document.createElement('dialog');
    dialog.className = 'pr-cart-recovery';
    const title = document.createElement('h2');
    title.textContent = labels.title;
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    message.textContent = lines ? labels.intro : labels.invalid;
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'button';
    action.textContent = labels.continue;
    action.hidden = !lines;
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = labels.close;
    close.addEventListener('click', () => dialog.close());
    dialog.append(title, message, action, close);
    document.body.append(dialog);
    dialog.showModal();
    let checkoutReady = false;
    action.addEventListener('click', async () => {
      if (checkoutReady) { location.href = routeRoot + 'checkout'; return; }
      action.disabled = true;
      message.textContent = labels.loading;
      try {
        let cart = await readCart();
        if (cart.items.length) {
          message.textContent = labels.preserved;
          action.textContent = labels.checkout;
          action.disabled = false;
          checkoutReady = true;
          return;
        }
        const response = await originalFetch(routeRoot + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: lines })
        });
        cart = await readCart();
        schedule('recovery');
        if (!response.ok || !matches(cart, lines)) {
          message.textContent = labels.unavailable;
          action.hidden = true;
          return;
        }
        message.textContent = labels.restored;
        action.textContent = labels.checkout;
        action.disabled = false;
        checkoutReady = true;
      } catch (_) {
        message.textContent = labels.error;
        action.disabled = false;
      }
    });
  }
  showRecovery();
  schedule('initial');
  if (qa && testEmail) {
    const savedEmail = testEmail;
    [1500, 5000].forEach(delay => setTimeout(() => {
      if (!testEnabled) enableTest(savedEmail).catch(() => {});
    }, delay));
  }
  if (!root.Shopify?.customerPrivacy && typeof root.Shopify?.loadFeatures === 'function') {
    root.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
      if (!error) schedule('privacy_ready');
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
